import { SearchFilterV4 } from "@/types/SearchFilter";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "@components/ui/dialog";
import { Trans } from "@lingui/react/macro";
import { DialogProps } from "@radix-ui/react-dialog";
import { Slider } from "@components/ui/slider";
import { Button } from "@components/ui/button";
import { useMemo, useState } from "react";
import { useI18n } from "@/contexts/i18nContext";
import { LOCALE } from "@/constants";
import { HELP_ARTICLES } from "@/help-articles";

export default function DialogReach({
  maxReach,
  onChange,
  value,
  ...rest
}: {
  onChange: (value: SearchFilterV4, clear?: boolean) => void;
  value: SearchFilterV4["min_reach_threshold"];
  maxReach: number;
} & DialogProps) {
  const { language, abbreviateNumber } = useI18n();

  // Local state
  const defaultValue = useMemo(() => {
    return value || 0;
  }, [value]);

  const [min, setMin] = useState<number>(defaultValue);

  // Side-effects
  const [prevDefaultValue, setPrevDefaultValue] = useState(defaultValue);
  const [prevOpen, setPrevOpen] = useState(rest.open);
  if (defaultValue !== prevDefaultValue || rest.open !== prevOpen) {
    setPrevDefaultValue(defaultValue);
    setPrevOpen(rest.open);
    setMin(defaultValue);
  }

  // Helpers
  const isDirty = defaultValue !== min;

  return (
    <Dialog {...rest}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Trans>Minsta räckvidd</Trans>
          </DialogTitle>
          <DialogDescription>
            <Trans>Filtrera omnämnanden på minsta räckvidd</Trans>
          </DialogDescription>
        </DialogHeader>

        <Slider
          data-testid="filter-bar--min-reach-threshold-input"
          onValueChange={(newValue) => setMin(newValue[0])}
          max={maxReach}
          value={[min]}
        />

        <div className="flex justify-between items-center ">
          <p>{abbreviateNumber(min)}</p>
          <p>{abbreviateNumber(maxReach)}</p>
        </div>

        <p className="text-muted-foreground text-balance text-sm">
          <Trans>
            Räckvidd är ett estimat för hur många personer som har nåtts av ett
            omnämnande. Läs mer om hur vi har räknat ut räckvidd{" "}
            <a
              href={
                language === LOCALE.SWEDISH
                  ? HELP_ARTICLES.REACH_SV
                  : HELP_ARTICLES.REACH_EN
              }
              rel="noopener noreferrer"
              target="_blank"
              className="link"
            >
              här
            </a>
            .
          </Trans>
        </p>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">
              <Trans>Avbryt</Trans>
            </Button>
          </DialogClose>
          <DialogClose
            disabled={!isDirty}
            onClick={() => {
              onChange({
                min_reach_threshold: min,
              });
            }}
            asChild
          >
            <Button data-testid="filter-bar--submit">
              <Trans>Applicera</Trans>
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
